"use client"

import { useMutation } from "@tanstack/react-query"
import { Evaluate } from "viem"
import { useConfig } from "wagmi"
import type { Permit2ErrorType } from "../actions/permit2"
import type {
  Permit2Data,
  Permit2Mutate,
  Permit2MutateAsync,
  Permit2Variables,
} from "../query"
import { permit2MutationOptions } from "../query"
import type { ConfigParameter } from "../types"
import type { UseMutationParameters, UseMutationReturnType } from "../utils"

export type UsePermit2Parameters<context = unknown> = Evaluate<
  ConfigParameter & {
    mutation?:
      | UseMutationParameters<
          Permit2Data,
          Permit2ErrorType,
          Permit2Variables,
          context
        >
      | undefined
  }
>

export type UsePermit2ReturnType<context = unknown> = Evaluate<
  UseMutationReturnType<
    Permit2Data,
    Permit2ErrorType,
    Permit2Variables,
    context
  > & {
    permit2: Permit2Mutate<context>
    permit2Async: Permit2MutateAsync<context>
  }
>

/** https://wagmi.sh/react/api/hooks/usePermit2 */
export function usePermit2<context = unknown>(
  parameters: UsePermit2Parameters<context> = {},
): UsePermit2ReturnType<context> {
  const { mutation } = parameters

  const config = useConfig(parameters)

  const mutationOptions = permit2MutationOptions(config)
  const { mutate, mutateAsync, ...result } = useMutation({
    ...mutation,
    ...mutationOptions,
  })

  return {
    ...result,
    permit2: mutate,
    permit2Async: mutateAsync,
  }
}
